import React, { useState } from 'react';
import { Upload, Zap, FileJson } from 'lucide-react';
import { API_URL } from '../config';

function DataUpload({ onAnalyze }) {
  const [dataSource, setDataSource] = useState('synthetic'); // synthetic | upload
  const [file, setFile] = useState(null);
  const [syntheticConfig, setSyntheticConfig] = useState({
    n_trajectories: 50,
    n_timesteps: 1000
  });

  const handleGenerateSynthetic = async () => {
    try {
      const response = await fetch(`${API_URL}/api/generate-synthetic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(syntheticConfig)
      });
      const data = await response.json();
      onAnalyze(data);
    } catch (error) {
      alert(`Failed to generate synthetic data: ${error.message}`);
    }
  };

  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        onAnalyze(data);
      } catch (error) {
        alert('Invalid JSON file');
      }
    };
    reader.readAsText(uploadedFile);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-gray-800/50 backdrop-blur rounded-lg p-8">
        <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
          <Upload className="w-6 h-6" />
          Choose Data Source
        </h2>

        {/* Data source tabs */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setDataSource('synthetic')}
            className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all ${
              dataSource === 'synthetic'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            <Zap className="w-5 h-5 inline mr-2" />
            Generate Synthetic
          </button>
          <button
            onClick={() => setDataSource('upload')}
            className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all ${
              dataSource === 'upload'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            <FileJson className="w-5 h-5 inline mr-2" />
            Upload JSON
          </button>
        </div>

        {/* Synthetic data config */}
        {dataSource === 'synthetic' && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-2">
                Number of Trajectories
              </label>
              <input
                type="number"
                value={syntheticConfig.n_trajectories}
                onChange={(e) => setSyntheticConfig({
                  ...syntheticConfig,
                  n_trajectories: parseInt(e.target.value)
                })}
                className="w-full px-4 py-2 bg-gray-700 rounded-lg"
                min="10"
                max="1000"
              />
              <p className="text-sm text-gray-400 mt-1">
                More trajectories = more jumps to analyze (slower)
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Timesteps per Trajectory
              </label>
              <input
                type="number"
                value={syntheticConfig.n_timesteps}
                onChange={(e) => setSyntheticConfig({
                  ...syntheticConfig,
                  n_timesteps: parseInt(e.target.value)
                })}
                className="w-full px-4 py-2 bg-gray-700 rounded-lg"
                min="100"
                max="10000"
              />
              <p className="text-sm text-gray-400 mt-1">
                More timesteps = finer temporal resolution
              </p>
            </div>
            <button
              onClick={handleGenerateSynthetic}
              className="w-full py-3 px-6 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all"
            >
              Generate & Analyze
            </button>
          </div>
        )}

        {/* File upload */}
        {dataSource === 'upload' && (
          <div className="space-y-6">
            <div className="border-2 border-dashed border-gray-600 rounded-lg p-12 text-center">
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-300 mb-4">
                Drop quantum trajectory JSON file here
              </p>
              <input
                type="file"
                accept=".json"
                onChange={handleFileUpload}
                className="block w-full text-sm text-gray-400
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:bg-purple-600 file:text-white
                  file:cursor-pointer hover:file:bg-purple-700"
              />
            </div>
            <div className="bg-gray-700/50 rounded-lg p-4 text-sm">
              <p className="font-semibold mb-2">Expected format:</p>
              <pre className="text-xs text-gray-300 overflow-x-auto">
{`{
  "source": "QuTiP" | "IBM Quantum",
  "trajectories": [
    {
      "trajectory_id": 0,
      "timesteps": [
        {
          "time": 0.0,
          "state": {
            "real": [1.0, 0.0],
            "imag": [0.0, 0.0]
          }
        }
      ]
    }
  ]
}`}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DataUpload;

